import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { http, NGN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ChartLineUp, Wallet, CheckSquare, Coins, Bell, SignOut, House, ChartLine, Copy, IdentificationCard, Users, Trophy, Share, DownloadSimple, ShieldWarning, ArrowRight, Medal } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { BACKEND_URL } from "@/lib/api";
import KycUpload from "@/components/KycUpload";

const downloadStatement = (invId, ref) => {
  const link = document.createElement("a");
  link.href = `${BACKEND_URL}/api/investments/${invId}/statement`;
  link.download = `mayoto-statement-${ref}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadCertificate = (invId, ref) => {
  const link = document.createElement("a");
  link.href = `${BACKEND_URL}/api/investments/${invId}/certificate`;
  link.download = `mayoto-certificate-${ref}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [pf, setPf] = useState(null);
  const [invs, setInvs] = useState([]);
  const [ref, setRef] = useState(null);
  const [board, setBoard] = useState([]);
  const [kyc, setKyc] = useState(null);
  const [tab, setTab] = useState("overview");
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    const t = sp.get("tab");
    if (t && ["overview","investments","kyc","referrals"].includes(t)) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeTab = (v) => {
    setTab(v);
    setSp((prev) => { const n = new URLSearchParams(prev); n.set("tab", v); return n; }, { replace: true });
  };

  const load = async () => {
    try {
      const [p, i, r, b, k] = await Promise.all([
        http.get("/investor/portfolio"), http.get("/investments/mine"),
        http.get("/referrals/mine"), http.get("/referrals/leaderboard"),
        http.get("/kyc/status"),
      ]);
      setPf(p.data); setInvs(i.data); setRef(r.data); setBoard(b.data); setKyc(k.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const copy = async (v, label = "Copied") => {
    try { await navigator.clipboard.writeText(v); toast.success(label); } catch { toast.error("Copy failed"); }
  };

  const stats = [
    { label: "Total Invested", value: NGN(pf?.total_investment || 0), icon: Wallet },
    { label: "Active", value: pf?.active_investments || 0, icon: ChartLineUp },
    { label: "Completed", value: pf?.completed_investments || 0, icon: CheckSquare },
    { label: "Referrals", value: ref?.referral_count || 0, icon: Users },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 mayo-navy-bg text-white flex-col p-6 z-40">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><span className="text-[#00b4d8] font-black text-2xl font-display">M</span></span>
          <span className="font-display font-black text-xl">MAYOTO</span>
        </Link>
        <p className="text-xs text-[#d4af37] font-display font-bold tracking-widest mb-4">INVESTOR CONSOLE</p>
        <nav className="flex-1 space-y-1">
          <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white bg-white/10"><House size={18} />Overview</Link>
          <Link to="/investments" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5"><ChartLineUp size={18} />Explore</Link>
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5"><House size={18} />Public site</Link>
        </nav>
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-[#00b4d8] grid place-items-center font-bold">{(user?.full_name || "M").slice(0,1).toUpperCase()}</div>
            <div className="min-w-0"><p className="text-sm font-semibold truncate">{user?.full_name}</p><p className="text-xs text-slate-400 truncate">{user?.email}</p></div>
          </div>
          <button onClick={async () => { await logout(); nav("/"); }} className="w-full text-left text-sm text-slate-300 hover:text-white flex items-center gap-2" data-testid="dashboard-logout"><SignOut size={16} /> Log out</button>
        </div>
      </aside>

      <main className="flex-1 lg:pl-64">
        <div className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="px-6 lg:px-10 h-16 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Investor Dashboard</p>
              <p className="font-display font-bold text-[#0a1128]" data-testid="dashboard-hello">Welcome, {user?.full_name?.split(" ")[0] || "Investor"}</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={`${(kyc?.kyc_status || "pending") === "verified" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}`} data-testid="kyc-status-badge">
                KYC: {kyc?.kyc_status || "pending"}
              </Badge>
              <Link to="/investments"><Button className="bg-[#00b4d8] hover:bg-[#0096c7] rounded-full">Invest Now</Button></Link>
            </div>
          </div>
        </div>

        <div className="p-6 lg:p-10">
          <Tabs value={tab} onValueChange={changeTab}>
            <TabsList className="bg-white border border-slate-200 p-1 rounded-full h-auto flex-wrap" data-testid="investor-tabs">
              <TabsTrigger value="overview" className="rounded-full data-[state=active]:bg-[#0a1128] data-[state=active]:text-white px-5 py-2" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="investments" className="rounded-full data-[state=active]:bg-[#0a1128] data-[state=active]:text-white px-5 py-2" data-testid="tab-investments">My Investments</TabsTrigger>
              <TabsTrigger value="kyc" className="rounded-full data-[state=active]:bg-[#0a1128] data-[state=active]:text-white px-5 py-2" data-testid="tab-kyc">KYC</TabsTrigger>
              <TabsTrigger value="referrals" className="rounded-full data-[state=active]:bg-[#0a1128] data-[state=active]:text-white px-5 py-2" data-testid="tab-referrals">Referrals</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-8 space-y-8">
              {user && user.email_verified === false && (
                <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="email-verify-nudge">
                  <div className="h-11 w-11 rounded-2xl bg-amber-100 grid place-items-center shrink-0">
                    <Bell size={20} className="text-amber-700" weight="bold" />
                  </div>
                  <div className="flex-1">
                    <p className="font-display font-bold text-amber-900 leading-tight">Confirm your email address</p>
                    <p className="text-sm text-amber-800 mt-1">
                      We sent a confirmation link to <span className="font-semibold">{user.email}</span>. Click the link inside to fully activate your account. Not in your inbox? Check spam, or ask us to verify you manually at <a href="mailto:mayotonexo@gmail.com" className="underline font-semibold">mayotonexo@gmail.com</a>.
                    </p>
                  </div>
                  <button
                    data-testid="email-verify-resend"
                    onClick={async () => {
                      try {
                        await http.post("/auth/email/resend");
                        toast.success("Fresh verification link sent — check your inbox.");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Could not resend verification email.");
                      }
                    }}
                    className="rounded-full bg-amber-900 hover:bg-amber-950 text-white text-sm font-semibold px-4 py-2 whitespace-nowrap"
                  >
                    Resend link
                  </button>
                </div>
              )}
              {kyc?.kyc_status !== "verified" && (
                <div className="rounded-2xl bg-gradient-to-r from-[#0a1128] to-[#00b4d8] text-white p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="kyc-nudge">
                  <div className="h-12 w-12 rounded-2xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
                    <ShieldWarning size={22} weight="bold" className="text-[#d4af37]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-display font-bold text-lg leading-tight">Complete your KYC before your next investment</p>
                    <p className="text-sm text-slate-200 mt-1">
                      {kyc?.submission?.status === "pending" ? "Your submission is under review — this usually takes 24-48 hours." :
                       kyc?.submission?.status === "rejected" ? "Your last submission was rejected. Please resubmit with a clearer document." :
                       "Verifying your identity unlocks higher investment limits and protects your account."}
                    </p>
                  </div>
                  <button
                    data-testid="kyc-nudge-cta"
                    onClick={() => changeTab("kyc")}
                    className="btn-shine bg-[#d4af37] hover:bg-[#c99a2e] text-[#0a1128] font-bold rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-1 whitespace-nowrap"
                  >
                    {kyc?.submission ? "View Status" : "Start KYC"} <ArrowRight size={16} />
                  </button>
                </div>
              )}
              {pf?.demo && (
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
                  {pf?.note}
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map(s => (
                  <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g,"-")}`}>
                    <div className="flex items-center justify-between"><p className="text-sm text-slate-500">{s.label}</p><s.icon size={20} className="text-[#00b4d8]" /></div>
                    <p className="mt-3 stat-number text-3xl text-[#0a1128]">{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
                  <p className="font-display font-bold text-lg text-[#0a1128]">Investment Performance (6-Month)</p>
                  <div className="h-64 mt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={pf?.performance_series || []}>
                        <defs><linearGradient id="grad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00b4d8" stopOpacity={0.4}/><stop offset="95%" stopColor="#00b4d8" stopOpacity={0}/></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={12} />
                        <Tooltip />
                        <Area type="monotone" dataKey="value" stroke="#00b4d8" strokeWidth={2} fill="url(#grad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <p className="font-display font-bold text-lg text-[#0a1128]">Notifications</p>
                  <div className="mt-4 space-y-3 max-h-72 overflow-y-auto">
                    {(pf?.notifications || []).map(n => (
                      <div key={n.id} className="p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid={`notif-${n.id}`}>
                        <p className="font-semibold text-sm text-[#0a1128]">{n.title}</p>
                        <p className="text-xs text-slate-600 mt-1">{n.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="investments" className="mt-8">
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <p className="font-display font-bold text-lg text-[#0a1128]">Payment History</p>
                  <Link to="/investments"><Button className="bg-[#00b4d8] hover:bg-[#0096c7] rounded-full">Start New Investment</Button></Link>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        {["Date","Investment","Amount","Status","Reference",""].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-bold tracking-wider text-slate-500 uppercase">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {invs.map((i, idx) => (
                        <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`inv-row-${idx}`}>
                          <td className="px-4 py-3 text-slate-700">{new Date(i.created_at).toLocaleDateString()}</td>
                          <td className="px-4 py-3 font-semibold text-[#0a1128]">{i.opportunity_name}</td>
                          <td className="px-4 py-3">{NGN(i.amount)}</td>
                          <td className="px-4 py-3">
                            <Badge className={i.status === "successful" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : i.status === "under_review" ? "bg-amber-100 text-amber-800 hover:bg-amber-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>
                              {i.status.replace("_"," ").toUpperCase()}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">{i.reference}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-2">
                              {i.status === "successful" && (
                                <>
                                  <button
                                    onClick={() => downloadStatement(i.id, i.reference)}
                                    className="inline-flex items-center gap-1 text-xs bg-[#0a1128] text-white hover:bg-[#071026] rounded-full px-3 py-1.5"
                                    data-testid={`inv-statement-${idx}`}
                                  >
                                    <DownloadSimple size={12} /> Statement
                                  </button>
                                  <button
                                    onClick={() => downloadCertificate(i.id, i.reference)}
                                    className="inline-flex items-center gap-1 text-xs bg-[#d4af37] text-[#0a1128] hover:bg-[#b8942d] rounded-full px-3 py-1.5 font-semibold"
                                    data-testid={`inv-certificate-${idx}`}
                                    title="Download shareable investor certificate"
                                  >
                                    <Medal size={12} weight="fill" /> Certificate
                                  </button>
                                </>
                              )}
                              <Link to={`/payment/${i.id}`} className="text-[#00b4d8] font-semibold text-xs hover:underline">View</Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {invs.length === 0 && (<tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No investments yet. <Link to="/investments" className="text-[#00b4d8] font-semibold">Explore opportunities →</Link></td></tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="kyc" className="mt-8">
              <KycUpload kyc={kyc} onDone={load} />
            </TabsContent>

            <TabsContent value="referrals" className="mt-8 space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-[#0a1128] text-white rounded-2xl p-8">
                  <p className="text-[#d4af37] font-display font-bold tracking-[0.25em] text-xs">YOUR REFERRAL CODE</p>
                  <p className="mt-4 font-display font-black text-4xl tracking-widest" data-testid="referral-code">{ref?.referral_code || "…"}</p>
                  <p className="mt-3 text-sm text-slate-300">Share this code with friends. When they register with your link, you climb the Annual Gala Awards leaderboard.</p>
                  <div className="mt-6 flex gap-2">
                    <button onClick={() => copy(ref?.referral_code, "Code copied")} className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full" data-testid="referral-copy-code"><Copy size={14} />Copy Code</button>
                    <button onClick={() => copy(ref?.share_url, "Link copied")} className="inline-flex items-center gap-1 text-xs bg-[#00b4d8] hover:bg-[#0096c7] px-4 py-2 rounded-full" data-testid="referral-copy-link"><Share size={14} />Copy Invite Link</button>
                  </div>
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <p className="text-slate-400 text-xs">People you have invited</p>
                    <p className="stat-number text-4xl mt-1">{ref?.referral_count || 0}</p>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-8">
                  <div className="flex items-center gap-2 mb-4"><Trophy size={20} className="text-[#d4af37]" weight="fill" /><p className="font-display font-bold text-lg text-[#0a1128]">Annual Gala — Most Referrals</p></div>
                  <ol className="space-y-3">
                    {board.slice(0, 5).map((b, i) => (
                      <li key={b.referral_code} className="flex items-center justify-between border-b border-slate-100 pb-2" data-testid={`board-${i}`}>
                        <div className="flex items-center gap-3">
                          <span className={`h-8 w-8 rounded-full grid place-items-center font-display font-black text-sm ${i === 0 ? "bg-[#d4af37] text-white" : "bg-slate-100 text-[#0a1128]"}`}>{i + 1}</span>
                          <span className="font-semibold text-[#0a1128] text-sm">{b.full_name}</span>
                        </div>
                        <span className="font-display font-black text-[#00b4d8]">{b.referral_count}</span>
                      </li>
                    ))}
                    {board.length === 0 && <p className="text-sm text-slate-500 text-center py-6">Be the first to invite a friend and appear on the leaderboard.</p>}
                  </ol>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
